/* Noire Atelier — global.js */
(function () {
  'use strict';

  window.NoireUtils = {
    debounce(fn, wait) {
      let t;
      return function (...args) {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), wait);
      };
    },
    announce(text) {
      const el = document.getElementById('A11yAnnouncer');
      if (!el) return;
      el.textContent = '';
      window.setTimeout(() => { el.textContent = text; }, 50);
    },
    prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    },
    getFocusable(container) {
      return Array.from(container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), summary, iframe, video[controls]'
      )).filter((el) => el.offsetParent !== null || el.closest('.modal, .drawer'));
    },
    trapFocus(container, initial) {
      this.removeTrapFocus();
      const focusables = this.getFocusable(container);
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      this._trapHandler = (e) => {
        if (e.key !== 'Tab') return;
        const list = this.getFocusable(container);
        if (!list.length) return;
        const f = list[0];
        const l = list[list.length - 1];
        if (e.shiftKey && document.activeElement === f) { e.preventDefault(); l.focus(); }
        else if (!e.shiftKey && document.activeElement === l) { e.preventDefault(); f.focus(); }
        else if (!container.contains(document.activeElement)) { e.preventDefault(); f.focus(); }
      };
      document.addEventListener('keydown', this._trapHandler);
      (initial || first || container).focus({ preventScroll: true });
    },
    removeTrapFocus() {
      if (this._trapHandler) {
        document.removeEventListener('keydown', this._trapHandler);
        this._trapHandler = null;
      }
    }
  };

  /* ---------- Overlay base (modal / drawer) ---------- */
  class OverlayElement extends HTMLElement {
    constructor() {
      super();
      this.trigger = null;
      this.onKeydown = this.onKeydown.bind(this);
    }
    connectedCallback() {
      this.root = this.querySelector('.modal, .drawer') || this;
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-modal-close], [data-drawer-close]')) {
          e.preventDefault();
          this.close();
        }
      });
    }
    get isOpen() { return this.root.classList.contains('is-open'); }
    open(trigger) {
      if (this.isOpen) return;
      this.trigger = trigger || document.activeElement;
      const shell = this.closest('[hidden]');
      if (shell) shell.hidden = false;
      this.root.classList.add('is-open');
      document.documentElement.classList.add('scroll-locked');
      document.addEventListener('keydown', this.onKeydown);
      const panel = this.root.querySelector('.modal__panel, .drawer__panel') || this.root;
      const initial = panel.querySelector('[data-autofocus]');
      window.requestAnimationFrame(() => window.NoireUtils.trapFocus(panel, initial));
      this.setTriggerExpanded(true);
      this.dispatchEvent(new CustomEvent('overlay:open', { bubbles: true }));
    }
    close() {
      if (!this.isOpen) return;
      this.root.classList.remove('is-open');
      document.documentElement.classList.remove('scroll-locked');
      document.removeEventListener('keydown', this.onKeydown);
      window.NoireUtils.removeTrapFocus();
      this.setTriggerExpanded(false);
      if (this.trigger && document.contains(this.trigger)) this.trigger.focus();
      this.trigger = null;
      this.dispatchEvent(new CustomEvent('overlay:close', { bubbles: true }));
    }
    setTriggerExpanded(value) {
      if (!this.id) return;
      document.querySelectorAll('[aria-controls="' + this.id + '"]').forEach((btn) => {
        btn.setAttribute('aria-expanded', value ? 'true' : 'false');
      });
    }
    onKeydown(e) {
      if (e.key === 'Escape') this.close();
    }
  }
  if (!customElements.get('theme-modal')) customElements.define('theme-modal', class extends OverlayElement {});
  if (!customElements.get('theme-drawer')) customElements.define('theme-drawer', class extends OverlayElement {});

  /* Openers */
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-open-drawer], [data-open-modal]');
    if (!opener) return;
    const id = opener.getAttribute('aria-controls');
    const target = id && document.getElementById(id);
    if (!target) return;
    const overlay = target.closest('theme-drawer, theme-modal') ||
      (target.matches('theme-drawer, theme-modal') ? target : target.querySelector('theme-drawer, theme-modal'));
    if (!overlay) return;
    e.preventDefault();
    overlay.open(opener);
    const autofocus = overlay.querySelector('[data-autofocus]');
    if (autofocus) window.setTimeout(() => autofocus.focus(), 60);
  });

  /* ---------- Sticky header ---------- */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.wrap = this.querySelector('.header-wrap');
      if (!this.wrap) return;
      this.mode = this.dataset.sticky || 'none';
      this.lastY = window.scrollY;
      this.setHeightVar();
      window.addEventListener('resize', window.NoireUtils.debounce(() => this.setHeightVar(), 150));
      if (this.mode === 'none' && !this.wrap.classList.contains('header-wrap--transparent')) return;
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }
    disconnectedCallback() {
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    }
    setHeightVar() {
      const header = this.querySelector('.header');
      if (header) document.documentElement.style.setProperty('--header-height', header.offsetHeight + 'px');
    }
    onScroll() {
      const y = window.scrollY;
      this.wrap.classList.toggle('is-stuck', y > 24);
      if (this.mode === 'on-scroll-up') {
        if (y > this.lastY && y > 160) this.wrap.classList.add('is-scrolled-down');
        else this.wrap.classList.remove('is-scrolled-down');
      }
      this.lastY = y;
    }
  }
  if (!customElements.get('sticky-header')) customElements.define('sticky-header', StickyHeader);

  /* Mega menu keyboard escape */
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const mega = e.target.closest && e.target.closest('.mega');
    if (mega) {
      const li = mega.closest('li');
      const link = li && li.querySelector('.header__menu-link');
      li.classList.remove('is-open');
      if (link) link.focus();
    }
  });

  /* ---------- Announcement slider ---------- */
  class AnnouncementSlider extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('.announcement__slide'));
      this.index = 0;
      if (!this.slides.length) return;
      this.show(0);
      if (this.slides.length < 2) return;
      this.interval = parseInt(this.dataset.interval || '5000', 10);
      this.pauseBtn = this.querySelector('[data-announcement-pause]');
      this.paused = window.NoireUtils.prefersReducedMotion();
      if (this.pauseBtn) {
        this.pauseBtn.addEventListener('click', () => this.toggle());
        this.updatePauseBtn();
      }
      const prev = this.querySelector('[data-announcement-prev]');
      const next = this.querySelector('[data-announcement-next]');
      if (prev) prev.addEventListener('click', () => this.step(-1, true));
      if (next) next.addEventListener('click', () => this.step(1, true));
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.play());
      this.addEventListener('focusin', () => this.stop());
      this.addEventListener('focusout', () => this.play());
      document.addEventListener('shopify:block:select', (e) => {
        const i = this.slides.findIndex((s) => s.contains(e.target) || s === e.target);
        if (i > -1) { this.paused = true; this.stop(); this.show(i); this.updatePauseBtn(); }
      });
      this.play();
    }
    disconnectedCallback() { this.stop(); }
    show(i) {
      this.index = (i + this.slides.length) % this.slides.length;
      this.slides.forEach((s, n) => s.classList.toggle('is-active', n === this.index));
    }
    step(dir, user) {
      this.show(this.index + dir);
      if (user) { this.paused = true; this.stop(); this.updatePauseBtn(); }
    }
    play() {
      if (this.paused || this.timer) return;
      this.timer = window.setInterval(() => this.show(this.index + 1), this.interval);
    }
    stop() { window.clearInterval(this.timer); this.timer = null; }
    toggle() {
      this.paused = !this.paused;
      this.paused ? this.stop() : this.play();
      this.updatePauseBtn();
    }
    updatePauseBtn() {
      if (!this.pauseBtn) return;
      this.pauseBtn.setAttribute('aria-pressed', this.paused ? 'true' : 'false');
      const play = this.pauseBtn.querySelector('[data-icon-play]');
      const pause = this.pauseBtn.querySelector('[data-icon-pause]');
      if (play) play.hidden = !this.paused;
      if (pause) pause.hidden = this.paused;
    }
  }
  if (!customElements.get('announcement-slider')) customElements.define('announcement-slider', AnnouncementSlider);

  /* ---------- Countdown ---------- */
  class CountdownTimer extends HTMLElement {
    connectedCallback() {
      const raw = (this.dataset.deadline || '').trim().replace(' ', 'T');
      this.deadline = raw ? new Date(raw).getTime() : NaN;
      this.wrap = this.closest('[data-countdown-wrap]') || this;
      if (isNaN(this.deadline) || this.deadline <= Date.now()) { this.wrap.hidden = true; return; }
      this.out = this.querySelector('[data-countdown-output]') || this;
      this.tick();
      this.timer = window.setInterval(() => this.tick(), 1000);
    }
    disconnectedCallback() { window.clearInterval(this.timer); }
    tick() {
      const diff = this.deadline - Date.now();
      if (diff <= 0) { window.clearInterval(this.timer); this.wrap.hidden = true; return; }
      const d = Math.floor(diff / 86400000);
      const h = Math.floor(diff / 3600000) % 24;
      const m = Math.floor(diff / 60000) % 60;
      const s = Math.floor(diff / 1000) % 60;
      const pad = (n) => String(n).padStart(2, '0');
      this.out.textContent = (d > 0 ? d + 'd ' : '') + pad(h) + ':' + pad(m) + ':' + pad(s);
    }
  }
  if (!customElements.get('countdown-timer')) customElements.define('countdown-timer', CountdownTimer);

  /* ---------- Quantity input ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      if (!this.input) return;
      this.querySelectorAll('button').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const step = parseInt(this.input.step || '1', 10) || 1;
          const min = this.input.min !== '' ? parseInt(this.input.min, 10) : 0;
          const max = this.input.max !== '' ? parseInt(this.input.max, 10) : Infinity;
          let val = parseInt(this.input.value, 10) || min;
          val += btn.dataset.qtyChange === 'inc' ? step : -step;
          val = Math.min(max, Math.max(min, val));
          if (String(val) !== this.input.value) {
            this.input.value = val;
            this.input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      });
    }
  }
  if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);

  /* ---------- Scroll-snap slider ---------- */
  class SliderComponent extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-slider-track]');
      this.prev = this.querySelector('[data-slider-prev]');
      this.next = this.querySelector('[data-slider-next]');
      if (!this.track) return;
      const update = () => this.updateButtons();
      if (this.prev) this.prev.addEventListener('click', () => this.scrollByPage(-1));
      if (this.next) this.next.addEventListener('click', () => this.scrollByPage(1));
      this.track.addEventListener('scroll', window.NoireUtils.debounce(update, 80), { passive: true });
      window.addEventListener('resize', window.NoireUtils.debounce(update, 150));
      update();
    }
    scrollByPage(dir) {
      const child = this.track.firstElementChild;
      const step = child ? child.getBoundingClientRect().width + 16 : this.track.clientWidth * 0.8;
      this.track.scrollBy({ left: step * dir, behavior: window.NoireUtils.prefersReducedMotion() ? 'auto' : 'smooth' });
    }
    updateButtons() {
      if (!this.prev || !this.next) return;
      const max = this.track.scrollWidth - this.track.clientWidth - 4;
      this.prev.disabled = this.track.scrollLeft <= 4;
      this.next.disabled = this.track.scrollLeft >= max;
      const hidden = this.track.scrollWidth <= this.track.clientWidth + 8;
      this.prev.parentElement.classList.toggle('visually-hidden', hidden);
    }
  }
  if (!customElements.get('slider-component')) customElements.define('slider-component', SliderComponent);

  /* ---------- Deferred media ---------- */
  class DeferredMedia extends HTMLElement {
    connectedCallback() {
      const poster = this.querySelector('[data-deferred-poster]');
      if (poster) poster.addEventListener('click', () => this.loadContent());
    }
    loadContent() {
      if (this.getAttribute('loaded')) return;
      const template = this.querySelector('template');
      if (!template) return;
      const content = template.content.cloneNode(true);
      this.setAttribute('loaded', 'true');
      this.appendChild(content);
      const media = this.querySelector('video, iframe');
      if (media) {
        media.focus();
        if (media.tagName === 'VIDEO') media.play().catch(() => {});
      }
      const poster = this.querySelector('[data-deferred-poster]');
      if (poster) poster.remove();
    }
  }
  if (!customElements.get('deferred-media')) customElements.define('deferred-media', DeferredMedia);

  /* Background autoplay videos: pause offscreen */
  const bgVideos = () => {
    const vids = document.querySelectorAll('video[data-autoplay-video], [data-autoplay-video] video');
    if (!vids.length || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const v = entry.target;
        if (entry.isIntersecting) v.play().catch(() => {});
        else v.pause();
      });
    }, { threshold: 0.1 });
    vids.forEach((v) => io.observe(v));
  };

  /* ---------- Back to top ---------- */
  class BackToTop extends HTMLElement {
    connectedCallback() {
      this.btn = this.querySelector('[data-back-to-top]');
      if (!this.btn) return;
      this.onScroll = () => { this.btn.hidden = window.scrollY < 640; };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.btn.addEventListener('click', () => {
        window.scrollTo({ top: 0, behavior: window.NoireUtils.prefersReducedMotion() ? 'auto' : 'smooth' });
      });
      this.onScroll();
    }
    disconnectedCallback() { window.removeEventListener('scroll', this.onScroll); }
  }
  if (!customElements.get('back-to-top')) customElements.define('back-to-top', BackToTop);

  /* ---------- Image compare ---------- */
  class ImageCompare extends HTMLElement {
    connectedCallback() {
      const range = this.querySelector('.compare__range');
      const box = this.querySelector('.compare');
      if (!range || !box) return;
      const set = () => box.style.setProperty('--compare-pos', range.value + '%');
      range.addEventListener('input', set);
      set();
    }
  }
  if (!customElements.get('image-compare')) customElements.define('image-compare', ImageCompare);

  /* ---------- Localization selectors ---------- */
  document.addEventListener('change', (e) => {
    const select = e.target.closest('[data-localization-select]');
    if (!select) return;
    const form = select.closest('form');
    if (form) form.submit();
  });

  /* ---------- Size calculator ---------- */
  class SizeCalculator extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.result = this.querySelector('[data-calc-result]');
      if (!this.form || !this.result) return;
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.compute();
      });
    }
    compute() {
      const band = parseFloat(this.form.querySelector('[name="band"]').value);
      const bust = parseFloat(this.form.querySelector('[name="bust"]').value);
      if (!band || !bust || bust < band) {
        this.result.hidden = false;
        this.result.innerHTML = '<p>' + (this.dataset.msgInvalid || '') + '</p>';
        return;
      }
      let bandSize = Math.round(band);
      if (bandSize % 2 !== 0) bandSize += 1;
      const diff = Math.round(bust) - bandSize;
      const cups = ['AA', 'A', 'B', 'C', 'D', 'DD', 'DDD'];
      let text;
      if (diff < 0) {
        text = this.dataset.msgConsult || '';
      } else if (diff > 6) {
        text = this.dataset.msgConsult || '';
      } else {
        const template = this.dataset.msgResult || '{{ size }}';
        text = template.replace('{{ size }}', bandSize + cups[diff]);
      }
      this.result.hidden = false;
      this.result.innerHTML = '<strong>' + text + '</strong><p>' + (this.dataset.msgDisclaimer || '') + '</p>';
    }
  }
  if (!customElements.get('size-calculator')) customElements.define('size-calculator', SizeCalculator);

  /* ---------- Section animations ---------- */
  const initAnimations = () => {
    if (!window.themeSettings || window.themeSettings.animations !== 'subtle') return;
    if (window.NoireUtils.prefersReducedMotion()) return;
    document.documentElement.classList.add('anim-subtle');
    if (!('IntersectionObserver' in window)) {
      document.querySelectorAll('[data-animate]').forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    document.querySelectorAll('[data-animate]').forEach((el) => io.observe(el));
  };

  /* ---------- Theme editor ---------- */
  document.addEventListener('shopify:section:select', (e) => {
    const overlay = e.target.querySelector('[data-open-in-design]');
    if (overlay && overlay.open) overlay.open();
  });
  document.addEventListener('shopify:section:deselect', (e) => {
    const overlay = e.target.querySelector('[data-open-in-design]');
    if (overlay && overlay.close) overlay.close();
  });
  document.addEventListener('shopify:section:load', () => {
    initAnimations();
    bgVideos();
  });

  document.addEventListener('DOMContentLoaded', () => {
    initAnimations();
    bgVideos();
  });
})();
